/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  AfterContentInit,
  Injectable,
} from '@angular/core';
import {InteractivityChecker} from './interactivity-checker';
import {Platform} from '../platform/platform';
import {coerceBooleanProperty} from '../coercion/boolean-property';

import'rxjs/add/operator/first';
/**
 * Class that allows for trapping focus within a DOM element.
 * 
 * NOTE: This class currently uses a very simple (naive) approach to focus trapping.
 * It assumes that the tab order is the same as DOM order, which is not necessarily true.
 * Things like tabIndex > 0, flex `order`, and shadow roots can cause to two to misalign.
 * This will be replaced with a more intelligent solution before the library is considered stable.
 */
export class FocusTrap {
private _startAnchor: HTMLElement;
private _endAnchor: HTMLElement;
/**
 * Whether the focus trap is active.
 * @return {?}
 */
get enabled(): boolean { return this._enabled; }
/**
 * @param {?} val
 * @return {?}
 */
set enabled(val: boolean) {
    this._enabled = val;

    if (this._startAnchor && this._endAnchor) {
      this._startAnchor.tabIndex = this._endAnchor.tabIndex = this._enabled ? 0 : -1;
    }
  }
private _enabled: boolean = true;
/**
 * @param {?} _element
 * @param {?} _platform
 * @param {?} _checker
 * @param {?} _ngZone
 * @param {?=} deferAnchors
 */
constructor(
private _element: HTMLElement,
private _platform: Platform,
private _checker: InteractivityChecker,
private _ngZone: NgZone,
    deferAnchors = false) {

    if (!deferAnchors) {
      this.attachAnchors();
    }
  }
/**
 * Destroys the focus trap by cleaning up the anchors.
 * @return {?}
 */
destroy() {
    if (this._startAnchor && this._startAnchor.parentNode) {
      this._startAnchor.parentNode.removeChild(this._startAnchor);
    }

    if (this._endAnchor && this._endAnchor.parentNode) {
      this._endAnchor.parentNode.removeChild(this._endAnchor);
    }

    this._startAnchor = this._endAnchor = null;
  }
/**
 * Inserts the anchors into the DOM. This is usually done automatically
 * in the constructor, but can be deferred for cases like directives with `*ngIf`.
 * @return {?}
 */
attachAnchors(): void {
    // If we're not on the browser, there can be no focus to trap.
    if (!this._platform.isBrowser) {
      return;
    }

    if (!this._startAnchor) {
      this._startAnchor = this._createAnchor();
    }

    if (!this._endAnchor) {
      this._endAnchor = this._createAnchor();
    }

    this._ngZone.runOutsideAngular(() => {
      this._startAnchor.addEventListener('focus', () => this.focusLastTabbableElement());
      this._endAnchor.addEventListener('focus', () => this.focusFirstTabbableElement());

      this._element.parentNode.insertBefore(this._startAnchor, this._element);
      this._element.parentNode.insertBefore(this._endAnchor, this._element.nextSibling);
    });
  }
/**
 * Waits for the zone to stabilize, then either focuses the first element that the
 * user specified, or the first tabbable element..
 * @return {?}
 */
focusInitialElementWhenReady() {
    this._executeOnStable(() => this.focusInitialElement());
  }
/**
 * Waits for the zone to stabilize, then focuses
 * the first tabbable element within the focus trap region.
 * @return {?}
 */
focusFirstTabbableElementWhenReady() {
    this._executeOnStable(() => this.focusFirstTabbableElement());
  }
/**
 * Waits for the zone to stabilize, then focuses
 * the last tabbable element within the focus trap region.
 * @return {?}
 */
focusLastTabbableElementWhenReady() {
    this._executeOnStable(() => this.focusLastTabbableElement());
  }
/**
 * Get the specified boundary element of the trapped region.
 * @param {?} bound The boundary to get (start or end of trapped region).
 * @return {?} The boundary element.
 */
private _getRegionBoundary(bound: 'start' | 'end'): HTMLElement | null {
    // Contains the deprecated version of selector, for temporary backwards comparability.
    let /** @type {?} */ markers = /** @type {?} */(( this._element.querySelectorAll(`[cdk-focus-region-${bound}], ` +
                                                 `[cdk-focus-${bound}]`) as NodeListOf<HTMLElement>));

    for (let /** @type {?} */ i = 0; i < markers.length; i++) {
      if (markers[i].hasAttribute(`cdk-focus-${bound}`)) {
        console.warn(`Found use of deprecated attribute 'cdk-focus-${bound}',` +
                     ` use 'cdk-focus-region-${bound}' instead.`, markers[i]);
      }
    }

    if (bound == 'start') {
      return markers.length ? markers[0] : this._getFirstTabbableElement(this._element);
    }
    return markers.length ?
        markers[markers.length - 1] : this._getLastTabbableElement(this._element);
  }
/**
 * Focuses the element that should be focused when the focus trap is initialized.
 * @return {?}
 */
focusInitialElement() {
    let /** @type {?} */ redirectToElement = /** @type {?} */(( this._element.querySelector('[cdk-focus-initial]') as HTMLElement));
    if (redirectToElement) {
      redirectToElement.focus();
    } else {
      this.focusFirstTabbableElement();
    }
  }
/**
 * Focuses the first tabbable element within the focus trap region.
 * @return {?}
 */
focusFirstTabbableElement() {
    let /** @type {?} */ redirectToElement = this._getRegionBoundary('start');
    if (redirectToElement) {
      redirectToElement.focus();
    }
  }
/**
 * Focuses the last tabbable element within the focus trap region.
 * @return {?}
 */
focusLastTabbableElement() {
    let /** @type {?} */ redirectToElement = this._getRegionBoundary('end');
    if (redirectToElement) {
      redirectToElement.focus();
    }
  }
/**
 * Get the first tabbable element from a DOM subtree (inclusive).
 * @param {?} root
 * @return {?}
 */
private _getFirstTabbableElement(root: HTMLElement): HTMLElement {
    if (this._checker.isFocusable(root) && this._checker.isTabbable(root)) {
      return root;
    }

    // Iterate in DOM order. Note that IE doesn't have `children` for SVG so we fall
    // back to `childNodes` which includes text nodes, comments etc.
    let /** @type {?} */ children = root.children || root.childNodes;

    for (let /** @type {?} */ i = 0; i < children.length; i++) {
      let /** @type {?} */ tabbableChild = children[i].nodeType === Node.ELEMENT_NODE ?
        this._getFirstTabbableElement( /** @type {?} */((children[i] as HTMLElement))) :
        null;

      if (tabbableChild) {
        return tabbableChild;
      }
    }

    return null;
  }
/**
 * Get the last tabbable element from a DOM subtree (inclusive).
 * @param {?} root
 * @return {?}
 */
private _getLastTabbableElement(root: HTMLElement): HTMLElement {
    if (this._checker.isFocusable(root) && this._checker.isTabbable(root)) {
      return root;
    }

    // Iterate in reverse DOM order.
    let /** @type {?} */ children = root.children || root.childNodes;

    for (let /** @type {?} */ i = children.length - 1; i >= 0; i--) {
      let /** @type {?} */ tabbableChild = children[i].nodeType === Node.ELEMENT_NODE ?
        this._getLastTabbableElement( /** @type {?} */((children[i] as HTMLElement))) :
        null;

      if (tabbableChild) {
        return tabbableChild;
      }
    }

    return null;
  }
/**
 * Creates an anchor element.
 * @return {?}
 */
private _createAnchor(): HTMLElement {
    let /** @type {?} */ anchor = document.createElement('div');
    anchor.tabIndex = this._enabled ? 0 : -1;
    anchor.classList.add('cdk-visually-hidden');
    anchor.classList.add('cdk-focus-trap-anchor');
    return anchor;
  }
/**
 * Executes a function when the zone is stable.
 * @param {?} fn
 * @return {?}
 */
private _executeOnStable(fn: () => any): void {
    if (this._ngZone.isStable) {
      fn();
    } else {
      this._ngZone.onStable.first().subscribe(fn);
    }
  }
}

function FocusTrap_tsickle_Closure_declarations() {
/** @type {?} */
FocusTrap.prototype._startAnchor;
/** @type {?} */
FocusTrap.prototype._endAnchor;
/** @type {?} */
FocusTrap.prototype._enabled;
/** @type {?} */
FocusTrap.prototype._element;
/** @type {?} */
FocusTrap.prototype._platform;
/** @type {?} */
FocusTrap.prototype._checker;
/** @type {?} */
FocusTrap.prototype._ngZone;
}

/**
 * Factory that allows easy instantiation of focus traps.
 */
export class FocusTrapFactory {
/**
 * @param {?} _checker
 * @param {?} _platform
 * @param {?} _ngZone
 */
constructor(
private _checker: InteractivityChecker,
private _platform: Platform,
private _ngZone: NgZone) { }
/**
 * @param {?} element
 * @param {?=} deferAnchors
 * @return {?}
 */
create(element: HTMLElement, deferAnchors = false): FocusTrap {
    return new FocusTrap(element, this._platform, this._checker, this._ngZone, deferAnchors);
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: InteractivityChecker, },
{type: Platform, },
{type: NgZone, },
];
}

function FocusTrapFactory_tsickle_Closure_declarations() {
/** @type {?} */
FocusTrapFactory.decorators;
/**
 * @nocollapse
 * @type {?}
 */
FocusTrapFactory.ctorParameters;
/** @type {?} */
FocusTrapFactory.prototype._checker;
/** @type {?} */
FocusTrapFactory.prototype._platform;
/** @type {?} */
FocusTrapFactory.prototype._ngZone;
}

/**
 * Directive for trapping focus within a region.
 * @deprecated
 */
export class FocusTrapDeprecatedDirective implements OnDestroy, AfterContentInit {
  focusTrap: FocusTrap;
/**
 * Whether the focus trap is active.
 * @return {?}
 */
get disabled(): boolean { return !this.focusTrap.enabled; }
/**
 * @param {?} val
 * @return {?}
 */
set disabled(val: boolean) {
    this.focusTrap.enabled = !coerceBooleanProperty(val);
  }
/**
 * @param {?} _elementRef
 * @param {?} _focusTrapFactory
 */
constructor(private _elementRef: ElementRef,
private _focusTrapFactory: FocusTrapFactory) {
    this.focusTrap = this._focusTrapFactory.create(this._elementRef.nativeElement, true);
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this.focusTrap.destroy();
  }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this.focusTrap.attachAnchors();
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'cdk-focus-trap',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: FocusTrapFactory, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'disabled': [{ type: Input },],
};
}

function FocusTrapDeprecatedDirective_tsickle_Closure_declarations() {
/** @type {?} */
FocusTrapDeprecatedDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
FocusTrapDeprecatedDirective.ctorParameters;
/** @type {?} */
FocusTrapDeprecatedDirective.propDecorators;
/** @type {?} */
FocusTrapDeprecatedDirective.prototype.focusTrap;
/** @type {?} */
FocusTrapDeprecatedDirective.prototype._elementRef;
/** @type {?} */
FocusTrapDeprecatedDirective.prototype._focusTrapFactory;
}

/**
 * Directive for trapping focus within a region.
 */
export class FocusTrapDirective implements OnDestroy, AfterContentInit {
  focusTrap: FocusTrap;
/**
 * Whether the focus trap is active.
 * @return {?}
 */
get enabled(): boolean { return this.focusTrap.enabled; }
/**
 * @param {?} value
 * @return {?}
 */
set enabled(value: boolean) { this.focusTrap.enabled = coerceBooleanProperty(value); }
/**
 * @param {?} _elementRef
 * @param {?} _focusTrapFactory
 */
constructor(private _elementRef: ElementRef,
private _focusTrapFactory: FocusTrapFactory) {
    this.focusTrap = this._focusTrapFactory.create(this._elementRef.nativeElement, true);
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this.focusTrap.destroy();
  }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this.focusTrap.attachAnchors();
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[cdkTrapFocus]',
  exportAs: 'cdkTrapFocus',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: FocusTrapFactory, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'enabled': [{ type: Input, args: ['cdkTrapFocus', ] },],
};
}

function FocusTrapDirective_tsickle_Closure_declarations() {
/** @type {?} */
FocusTrapDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
FocusTrapDirective.ctorParameters;
/** @type {?} */
FocusTrapDirective.propDecorators;
/** @type {?} */
FocusTrapDirective.prototype.focusTrap;
/** @type {?} */
FocusTrapDirective.prototype._elementRef;
/** @type {?} */
FocusTrapDirective.prototype._focusTrapFactory;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
